import AgentService from '@/services/agentService';
import BusinessProcessService from '@/services/businessProcessService';
import StatisticsService from '@/services/statisticsService';

const socketEventActions = {
  SOCKET_EVENT_AGENT_STATE_CHANGED_EVENT({ commit }, event) {
    commit('setLastKnownAgentStateChangeTimestamp', event);
  },

  SOCKET_EVENT_CHANGED_AGENT_KPI_EVENT({ commit }, { changedAgentData }) {
    changedAgentData.forEach(kpi => {
      commit('setAgentStatValue', {
        agentId: kpi.agentId,
        value: kpi.value,
        field: kpi.counterName[0].toLowerCase() + kpi.counterName.slice(1)
      });
    });
  },

  SOCKET_EVENT_CHANGED_BP_KPI_EVENT({ commit, getters }, { changedBusinessProcessData }) {
    changedBusinessProcessData.forEach(kpi => {
      const bpStats = getters.getBusinessProcessStatsById(kpi.businessProcessId);
      if (!bpStats) {
        // Ignoring stats update while the initial state is being loaded.
        return;
      }

      const newData = processKpiData(kpi, bpStats);

      if (newData.businessProcessId && newData.counterName) {
        commit('setBusinessProcessStatValue', {
          businessProcessId: newData.businessProcessId,
          value: newData.value,
          field: newData.counterName[0].toLowerCase() + newData.counterName.slice(1)
        });
      }
    });
  },

  SOCKET_EVENT_COMPLETE_AGENT_KPI_EVENT({ commit }, { agentData }) {
    commit('updateAgentsStats', agentData);
  },

  SOCKET_EVENT_COMPLETE_BP_KPI_EVENT({ commit }, { businessProcessData }) {
    commit('updateBusinessProcessStats', businessProcessData);
  },

  SOCKET_EVENT_COUNTER_UPDATE_EVENT({ commit, dispatch }, event) {
    event.counters.forEach(counter => commit('setKpiSubscriptionCounter', counter));
  }
};

let lastAgentUpdate, lastBusinessProcessUpdate;

const stateActions = {
  updateAgentsStats({ commit }) {
    const now = Date.now();
    if (lastAgentUpdate && now - lastAgentUpdate < 1000) {
      return;
    }
    lastAgentUpdate = now;
    return AgentService.getAgentsStats()
      .then(response => {
        if (response && response.data) {
          commit('updateAgentsStats', response.data);
        } else {
          console.log('updateAgentsStats error', response);
        }
        return response;
      })
      .catch(console.log);
  },

  updateBusinessProcessStats({ commit }) {
    const now = Date.now();
    if (lastBusinessProcessUpdate && now - lastBusinessProcessUpdate < 1000) {
      return;
    }
    lastBusinessProcessUpdate = now;
    return BusinessProcessService.getBusinessProcessStats()
      .then(response => {
        if (response && response.data) {
          commit('updateBusinessProcessStats', response.data);
        } else {
          console.log('updateBusinessProcessStats error', response);
        }
        return response;
      })
      .catch(console.log);
  },

  clearStats({ commit }) {
    commit('clearStats');
  },

  /**
   * Subscribe to a KPI counter.
   * @param commit - Vuex - Commit a mutation
   * @param rootGetters - Vuex - Getters from the root store
   * @param counterTypeId - Required. Specify the counter that will receive the updates. E.g. Business Process Total Interactions.
   * @param handlerId - Required. If the counter is related to an agent, it will be the agentId. If it is related to a Business Process, it will be the businessProcessId.
   * @param dimensionId - Optional. The id of the dimension that the component is interested in. E.g. Telephony, Chat, Email, etc.
   * If dimensionId is not provided, the component will receive the total value of the counter, which is the sum of all dimensions.
   *
   * Examples:
   * {
   *   counterTypeId: '45b331c6-e5de-44e5-a16c-d2d73d0ccf4f', <-- Required - Business Process Total Interactions
   *   handlerId: '0ee0c4c1-7d6d-4593-a798-94e10fddbe76',     <-- Required - businessProcessId
   * },
   * {
   *   counterTypeId: '45b331c6-e5de-44e5-a16c-d2d73d0ccf4f', <-- Required - Business Process Total Interactions
   *   handlerId: '0ee0c4c1-7d6d-4593-a798-94e10fddbe76',     <-- Required - businessProcessId
   *   dimensionId: 'd6ee7043-c6f9-47b2-aaf1-0e63ef684d97'    <-- Optional - Telephony
   * },
   * {
   *   counterTypeId: 'e1604428-6266-4c5a-86f0-ecfd4b9a7e8e'  <-- Required - Agent Break Time
   *   handlerId: 'e4705a54-2b42-451f-bcc2-95479c3cce26'      <-- Required - agentId
   * },
   * {
   *   counterTypeId: 'c374d308-a0fa-4300-8c66-64eb0b1034c0'  <-- Required - Agent BP Answered Calls
   *   handlerId: 'e4705a54-2b42-451f-bcc2-95479c3cce26'      <-- Required - agentId
   *   dimensionId: 'f6fb0a24-4c00-43d7-b795-55fc66b05f7c'    <-- Optional - Incoming
   * }
   */
  addKpiSubscription({ commit, dispatch }, { counterTypeId, handlerId, dimensionId = undefined }) {
    if (!handlerId || !counterTypeId) {
      return console.error('handlerId and counterTypeId are required');
    }
    commit('addKpiSubscription', { handlerId, counterTypeId, dimensionId });
  },

  /**
   * Remove a subscription
   * @param commit - Vuex - Commit a mutation
   * @param rootGetters - Vuex - Getters from the root store
   * @param counterTypeId - Required. Specify the counter that will receive the updates. E.g. Business Process Total Interactions.
   * @param handlerId - Required. If the counter is related to an agent, it will be the agentId. If it is related to a Business Process, it will be the businessProcessId.
   * @param dimensionId - Optional. The id of the dimension that the component is interested in. E.g. Telephony, Chat, Email, etc.
   */
  removeKpiSubscription({ commit, dispatch }, { counterTypeId, handlerId, dimensionId = undefined }) {
    if (!handlerId || !counterTypeId) {
      return console.error('handlerId and counterTypeId are required');
    }
    commit('removeKpiSubscription', { counterTypeId, handlerId, dimensionId });
  },

  /**
   * Consolidates all KPI subscriptions and commit them to the server.
   * [IMPORTANT] Triggered AUTOMATICALLY after a KPI subscription is added or removed
   *
   * @returns {Promise} - The promise returned by the server.
   */
  commitKpiSubscription({ rootGetters, state }) {
    const loggedInAgent = rootGetters['AGENT/loggedInAgent'];
    const subscriptions = [];

    Object.keys(state.subscribedCounters).forEach(counterTypeId => {
      Object.keys(state.subscribedCounters[counterTypeId]).forEach(handlerId => {
        if (state.subscribedCounters[counterTypeId][handlerId].refCount) {
          subscriptions.push({ counterTypeId, handlerId });
        }
        Object.keys(state.subscribedCounters[counterTypeId][handlerId]).forEach(dimensionId => {
          if (dimensionId !== 'refCount' && dimensionId !== 'value' && dimensionId !== 'secondaryValue') {
            subscriptions.push({ counterTypeId, handlerId, dimensionId });
          }
        });
      });
    });

    return StatisticsService.subscribeForCounters({
      clientSubscriptionId: loggedInAgent.id,
      handlerCounterPairs: subscriptions
    });
  },

  clearLastKnownAgentStateChangeTimestamps({ commit }) {
    commit('clearLastKnownAgentStateChangeTimestamps');
  }
};

const processKpiData = (kpi, currentData) => {
  if (!currentData) {
    return {};
  }

  const newKpiData = structuredClone(kpi);
  const field = newKpiData.counterName[0].toLowerCase() + newKpiData.counterName.slice(1);

  if (newKpiData.value.dimensionsValues) {
    newKpiData.value.dimensionsValues = currentData[field].dimensionsValues.map(dimension => {
      const kpiData = newKpiData.value.dimensionsValues.find(item => item.name === dimension.name);
      if (kpiData) {
        return kpiData;
      } else {
        return dimension;
      }
    });
  }

  return newKpiData;
};

export default {
  ...socketEventActions,
  ...stateActions
};
